import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CreditCard, Clock, PackagePlus, Lock } from "lucide-react";

/**
 * Dialog che si apre quando un distributore crea un ticket per un dispenser fuori garanzia
 * senza ore residue nel carnet. Mostra le 2 opzioni di acquisto (1h singola / carnet 10h)
 * e avvia il checkout Stripe.
 */
export function PayHoursDialog({ open, onOpenChange, ticketId, payment_options }) {
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState(null);

  const buy = async (packageKey) => {
    setChoice(packageKey);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/hours/checkout", {
        package: packageKey,
        origin_url: window.location.origin,
      });
      // Persist ticket link intent so we can associate the payment on return
      if (ticketId) {
        try {
          sessionStorage.setItem("pending_hours_ticket_id", ticketId);
          sessionStorage.setItem("pending_hours_session_id", data.session_id);
        } catch { /* ignore */ }
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
      setChoice(null);
    }
  };

  if (!payment_options) return null;
  const single = payment_options.single_hour;
  const carnet = payment_options.carnet_10h;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="pay-hours-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600" />
            <DialogTitle className="font-display tracking-tight">Ticket in attesa di pagamento</DialogTitle>
          </div>
        </DialogHeader>
        <div className="text-sm text-zinc-600 -mt-2">
          Il dispenser è <strong>fuori garanzia</strong> e nel tuo carnet non ci sono ore residue.
          Acquista un pacchetto per sbloccare il ticket e proseguire con l&apos;assistenza.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Card
            className={`rounded-sm border-zinc-200 p-5 cursor-pointer hover:border-brand ${choice === single?.package ? "ring-2 ring-brand" : ""}`}
            data-testid="pay-single-hour-card"
            onClick={() => !busy && buy(single.package)}
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-brand" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Ora singola</div>
            </div>
            <div className="font-display text-3xl font-bold tracking-tighter text-zinc-950">
              1<span className="text-lg font-normal ml-1 text-zinc-500">ora</span>
            </div>
            <div className="mt-1 text-xl font-semibold text-brand">€{single.amount.toFixed(2)}</div>
            <div className="text-xs text-zinc-500 mt-2">Ideale per un intervento singolo.</div>
            <Button
              className="w-full mt-4 bg-brand hover:bg-brand-dark text-white rounded-sm"
              disabled={busy}
              data-testid="pay-single-hour-btn"
            >
              {busy && choice === single.package ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirect...</> : <><CreditCard className="h-4 w-4 mr-2" /> Paga €{single.amount.toFixed(2)}</>}
            </Button>
          </Card>

          <Card
            className={`rounded-sm border-brand p-5 cursor-pointer bg-brand-muted/40 hover:bg-brand-muted ${choice === carnet?.package ? "ring-2 ring-brand" : ""}`}
            data-testid="pay-carnet-10h-card"
            onClick={() => !busy && buy(carnet.package)}
          >
            <div className="flex items-center gap-2 mb-2">
              <PackagePlus className="h-4 w-4 text-brand" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-medium">Carnet 10 ore · CONSIGLIATO</div>
            </div>
            <div className="font-display text-3xl font-bold tracking-tighter text-zinc-950">
              10<span className="text-lg font-normal ml-1 text-zinc-500">ore</span>
            </div>
            <div className="mt-1 text-xl font-semibold text-brand">€{carnet.amount.toFixed(2)}</div>
            <div className="text-xs text-zinc-500 mt-2">
              Prezzo/h €{(carnet.amount / 10).toFixed(2)} · Risparmio €{(single.amount * 10 - carnet.amount).toFixed(2)}
            </div>
            <Button
              className="w-full mt-4 bg-brand hover:bg-brand-dark text-white rounded-sm"
              disabled={busy}
              data-testid="pay-carnet-10h-btn"
            >
              {busy && choice === carnet.package ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirect...</> : <><CreditCard className="h-4 w-4 mr-2" /> Paga €{carnet.amount.toFixed(2)}</>}
            </Button>
          </Card>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy}>
            Pago dopo (ticket resterà in attesa)
          </Button>
        </DialogFooter>

        <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          Pagamento sicuro tramite Stripe. Le ore vengono accreditate automaticamente al tuo carnet appena il pagamento è confermato.
        </div>
      </DialogContent>
    </Dialog>
  );
}
